/**
 * Audio pads, the runtime half (2026-10-13). The item (`AudioPadsItem`) holds config; this file plays pads through
 * OPBridge's `/pads/*` routes (capability `audio_pads`, PROTOCOL.md "Audio pads") and mirrors what is sounding from
 * `GET /pads/status`, polled only while a pads item is on screen.
 *
 * Everything goes to the PRIMARY bridge with `pc` naming the PC that makes the sound, the same way test tracks play
 * (file sync puts every library file on every PC). A pad's id is `<item id>:<index>`, so OPBridge's voices map back
 * to the pad that started them.
 */
import { createSignal } from 'solid-js';
import { connectionsVersion, getConnection, primaryBridge, primaryHttpClient } from '../opbridge/store';
import { panicLatched } from '../performance/panicLatch.ts';
import type { AudioKind, PadPlayAnswer, PadsStatus, PadVoice, TestTrack, TestTrackBeatmap } from '../opbridge/httpClient';
import type { AudioPad, AudioPadMode, AudioPadQuant, AudioPadWarp, AudioPadsItem } from '../layout/projectStore';
import { beatmaps, loadTrack, refreshTracks, tracks } from '../timeline/testTrackStore';
import { kindOf } from '../timeline/audioKind';
import { beatsPerBar, gridBpm } from '../timeline/testTrack';
import { pushToast } from '../ui/toast';

/** What a pad does when its own field is absent, by its file's type (operator: one-shots fire now, loops sit on the
 *  bar at Ableton's tempo, a song starts on the bar at its own speed). */
export function padDefaults(kind: AudioKind): { mode: AudioPadMode; quant: AudioPadQuant; warp: AudioPadWarp } {
  if (kind === 'loop') return { mode: 'loop', quant: 'bar', warp: 'repitch' };
  if (kind === 'song') return { mode: 'oneshot', quant: 'bar', warp: 'off' };
  return { mode: 'oneshot', quant: 'now', warp: 'off' };
}

export const padId = (item: Pick<AudioPadsItem, 'id'>, index: number) => `${item.id}:${index}`;

export function trackOf(pad: AudioPad | undefined): TestTrack | undefined {
  return pad?.trackId ? tracks().find((t) => t.id === pad.trackId) : undefined;
}

export function kindOfPad(pad: AudioPad | undefined): AudioKind | null {
  const t = trackOf(pad);
  return t ? kindOf(t, beatmaps()[t.id]) : null;
}

/** The pad's settings with its type's defaults filled in. */
export function resolvedPad(pad: AudioPad | undefined): { mode: AudioPadMode; quant: AudioPadQuant; warp: AudioPadWarp; gain: number } {
  const d = padDefaults(kindOfPad(pad) ?? 'oneshot');
  return { mode: pad?.mode ?? d.mode, quant: pad?.quant ?? d.quant, warp: pad?.warp ?? d.warp, gain: pad?.gain ?? 1 };
}

/** The tempo a loop was recorded at, from its LENGTH: whole bars (1, 2, 4 ... 32) x beats x 60 / seconds, the bar
 *  count being the one whose tempo is nearest the beat map's. OPBridge asked for this because a short file's grid can
 *  read at half tempo; so a tempo the operator has not corrected is first folded into 85..170 BPM, where a loop's
 *  tempo almost always is. Null when no whole number of bars lands within 8% of it. */
export function loopBpm(durationSec: number, map: TestTrackBeatmap | null | undefined): number | null {
  if (!map || !(durationSec > 0)) return null;
  let target = gridBpm(map);
  if (!(target > 0)) return null;
  if ((map.tempoChoice ?? 'as-read') === 'as-read') {
    while (target < 85) target *= 2;
    while (target >= 170) target /= 2;
  }
  let best: number | null = null;
  let err = Infinity;
  for (const bars of [1, 2, 4, 8, 16, 32]) {
    const bpm = (bars * beatsPerBar(map) * 60) / durationSec;
    const e = Math.abs(Math.log(bpm / target));
    if (e < err) { err = e; best = bpm; }
  }
  return err < Math.log(1.08) ? best : null;
}

/** Whether the primary OPBridge can play pads. Reactive. */
export function padsSupported(): boolean {
  // The connection map is not reactive: without this read an effect that ran before OPBridge's connection existed
  // never re-runs when it appears (found by audio_pads step 1: no preload, padsSupported() true when asked later).
  void connectionsVersion();
  const p = primaryBridge();
  return !!p && getConnection(p.id)?.health()?.capabilities?.['audio_pads'] === true;
}

/** Why pads cannot play right now, in the operator's words, or null. Reactive. */
export function padsUnavailable(): string | null {
  void connectionsVersion();
  const p = primaryBridge();
  if (!p) return 'No OPBridge';
  const conn = getConnection(p.id);
  if (!conn || conn.statusSignal()() !== 'connected') return 'OPBridge offline';
  if (!padsSupported()) return 'Update OPBridge';
  return null;
}

// ---- status, per PC ('' = the primary bridge's own PC) ----

const [statusByPc, setStatusByPc] = createSignal<Record<string, PadsStatus>>({});
/** Pads pressed but not in a status reply yet (shown as starting, so a tap never looks ignored). */
const [pending, setPending] = createSignal<Record<string, number>>({});
export { statusByPc };

export function voiceOf(pc: string | undefined, id: string): PadVoice | undefined {
  return statusByPc()[pc ?? '']?.voices.find((v) => v.padId === id);
}
export function isPending(id: string): boolean {
  const at = pending()[id];
  return at !== undefined && Date.now() - at < 3000;
}
const clearPending = (id: string) => setPending((o) => { if (!(id in o)) return o; const n = { ...o }; delete n[id]; return n; });

const watchers = new Map<string, number>();
let timer: ReturnType<typeof setTimeout> | null = null;

async function tick(): Promise<void> {
  timer = null;
  const c = primaryHttpClient();
  if (c && padsSupported()) {
    await Promise.all([...watchers.keys()].map(async (pc) => {
      try {
        const s = await c.padsStatus(pc || undefined);
        const voices = Array.isArray(s?.voices) ? s.voices : [];
        setStatusByPc((o) => ({ ...o, [pc]: { ...s, voices } }));
        for (const v of voices) clearPending(v.padId);
      } catch { /* a missed poll is not worth showing; the next one tries again */ }
    }));
  }
  if (watchers.size === 0) return;
  // Quick while anything sounds or was just pressed (the progress line), slow when idle.
  const busy = Object.values(statusByPc()).some((s) => s.voices.length > 0) || Object.keys(pending()).length > 0;
  timer = setTimeout(() => void tick(), busy ? 150 : 1000);
}

/** Poll `pc`'s pads while the returned release has not been called (one per pads item on screen). */
export function watchPads(pc: string | undefined): () => void {
  const key = pc ?? '';
  watchers.set(key, (watchers.get(key) ?? 0) + 1);
  if (tracks().length === 0) void refreshTracks();
  if (!timer) timer = setTimeout(() => void tick(), 0);
  return () => {
    const n = (watchers.get(key) ?? 1) - 1;
    if (n <= 0) watchers.delete(key); else watchers.set(key, n);
    if (watchers.size === 0 && timer) { clearTimeout(timer); timer = null; }
  };
}
const kick = () => { if (timer) clearTimeout(timer); timer = setTimeout(() => void tick(), 40); };

// ---- play / release / stop ----

/** OPBridge's refusal codes, in words the operator can act on. */
function why(e: unknown): string {
  const text = e instanceof Error ? e.message : String(e);
  let code = '', msg = '';
  const at = text.indexOf('{');
  if (at >= 0) {
    try { const b = JSON.parse(text.slice(at)) as { error?: string; message?: string }; code = b.error ?? ''; msg = b.message ?? ''; }
    catch { /* not OPBridge's JSON */ }
  }
  switch (code) {
    case 'no_tempo': return "Ableton's tempo is not known yet, so it cannot launch on the beat";
    case 'arriving': return 'The file is still copying to that computer. Try again in a moment';
    case 'output': return msg || 'That audio output did not open';
    case 'decode': return 'That file could not be read';
    case 'not_found': return 'That file is no longer in the audio library';
  }
  return msg || text;
}

let lastStoppedToast = 0;
let lastMutedToast = 0;

/** Press a pad. A playing LOOP stops instead (a loop pad is a toggle); everything else (re)starts. */
export async function pressPad(item: AudioPadsItem, index: number): Promise<void> {
  const pad = item.pads[index];
  const t = trackOf(pad);
  if (!pad?.trackId) { pushToast('warn', 'This pad has no file yet: pick one in the Inspector, or drop a file on it'); return; }
  // PANIC LOCKED blocks every send (panicLatch.ts). Pads reach OPBridge
  // through their own client, past the shared gate in `resolveBridges`, so
  // they played on while the banner said "all sending blocked" (audit
  // 2026-11-03 A-8). Stopping a pad stays allowed: it silences.
  if (panicLatched()) return;
  const id = padId(item, index);
  const r = resolvedPad(pad);
  const sounding = voiceOf(item.pc, id);
  if (r.mode === 'loop' && sounding && sounding.state !== 'releasing') { await stopPad(item, index); return; }
  const c = primaryHttpClient();
  if (!c) return;
  let bpm: number | undefined;
  if (r.warp === 'repitch' && t) {
    if (!(t.id in beatmaps())) await loadTrack(t.id);
    bpm = loopBpm(t.durationSec, beatmaps()[t.id]) ?? undefined;
  }
  setPending((o) => ({ ...o, [id]: Date.now() }));
  kick();
  let ans: PadPlayAnswer;
  try {
    ans = await c.padsPlay({
      padId: id, trackId: pad.trackId,
      ...(item.pc ? { pc: item.pc } : {}),
      ...(item.output ? { output: item.output } : {}),
      mode: r.mode, quant: r.quant, gain: r.gain, warp: r.warp,
      ...(bpm ? { bpm } : {}),
    });
  } catch (e) {
    clearPending(id);
    pushToast('error', `Pad did not play: ${why(e)}`);
    return;
  }
  const now = Date.now();
  if (ans.quant?.fellBack === 'clock_stopped' && r.quant !== 'now' && now - lastStoppedToast > 5000) {
    lastStoppedToast = now;
    pushToast('warn', "Played now: Ableton's clock is stopped, so there was no bar to wait for", 2500);
  }
  if (ans.muted && now - lastMutedToast > 5000) {
    lastMutedToast = now;
    pushToast('warn', 'Pads are muted on that computer (Inspector > Pads level)', 3000);
  }
  kick();
}

/** End a held (gate) pad. */
export async function releasePad(item: AudioPadsItem, index: number): Promise<void> {
  const c = primaryHttpClient();
  if (!c) return;
  try { await c.padsRelease(padId(item, index), item.pc); } catch { /* a release that missed: the voice ends at its file's end */ }
  kick();
}

export async function stopPad(item: AudioPadsItem, index: number): Promise<void> {
  const c = primaryHttpClient();
  if (!c) return;
  const id = padId(item, index);
  clearPending(id);
  try { await c.padsStop({ padId: id, ...(item.pc ? { pc: item.pc } : {}) }); } catch (e) { pushToast('error', `Pad did not stop: ${why(e)}`); }
  kick();
}

/** Stop every pad on every PC (the item's Stop all, and PANIC). Never throws. */
export async function stopAllPads(): Promise<boolean> {
  const c = primaryHttpClient();
  if (!c || !padsSupported()) return false;
  try { await c.padsStop({ all: true }); kick(); return true; } catch { return false; }
}

/** Decode the item's files ahead on its PC and open the output, so the first press is quick. Once per set of
 *  files and target; never throws. */
const preloaded = new Set<string>();
export function preloadPads(item: AudioPadsItem): void {
  const ids = [...new Set(item.pads.map((p) => p.trackId).filter((x): x is string => !!x))].sort();
  if (ids.length === 0 || !padsSupported()) return;
  // The client first: OPBridge's health can arrive before its client exists, and a key marked before that would
  // skip every later try (audio_pads step 1, 2026-10-13).
  const c = primaryHttpClient();
  if (!c) return;
  const key = `${item.pc ?? ''}|${item.output ?? ''}|${ids.join(',')}`;
  if (preloaded.has(key)) return;
  preloaded.add(key);
  c.padsPreload(ids, item.output, item.pc).catch(() => preloaded.delete(key));
}

// ---- the PC's pads level and mute ----

export async function readPadsSettings(pc: string | undefined): Promise<{ level: number; muted: boolean } | null> {
  const c = primaryHttpClient();
  if (!c || !padsSupported()) return null;
  try { const s = await c.padsSettings(pc); return { level: s.level ?? 1, muted: !!s.muted }; } catch { return null; }
}

export async function writePadsSettings(pc: string | undefined, next: { level?: number; muted?: boolean }): Promise<boolean> {
  const c = primaryHttpClient();
  if (!c) return false;
  try { await c.setPadsSettings({ ...next, ...(pc ? { pc } : {}) }); return true; }
  catch (e) { pushToast('error', `OPBridge did not save it: ${why(e)}`); return false; }
}

// ---- output latency (beside the output pickers) ----

export async function measureLatency(output: string, pc: string | undefined): Promise<number | null> {
  const c = primaryHttpClient();
  if (!c) return null;
  try { return (await c.measureOutputLatency(output, pc)).latencyMs; }
  catch (e) { pushToast('error', `Could not measure it: ${why(e)}`); return null; }
}

export async function saveLatency(output: string, ms: number, pc: string | undefined): Promise<boolean> {
  const c = primaryHttpClient();
  if (!c) return false;
  try { await c.setOutputLatency(output, ms, pc); return true; }
  catch (e) { pushToast('error', `OPBridge did not save it: ${why(e)}`); return false; }
}
